import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatApiErrorDetail } from '../../utils/axios';
import { AdminSidebar } from './AdminLeaderboardWinners';
import { Plus, Edit2, Trash2, X, Save, Calendar, Trophy } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  league: '', season: '2025/26', round: '', group_name: '', matchday: '',
  home_team_id: '', away_team_id: '', kickoff: '', venue: '', country_id: '',
  match_status: 'scheduled', prediction_enabled: true, home_score: '', away_score: '',
  description: '', visibility: 'published',
  total_prize: '', prize_label: 'TEBAK SCORE', prize_note: 'HADIAH EVENT TERVERIFIKASI',
  prize_verified: true, prize_breakdown: [],
};

export const AdminMatches = () => {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        axios.get(`${API_BASE}/admin/matches?limit=200`, { withCredentials: true }),
        axios.get(`${API_BASE}/admin/teams?limit=200`, { withCredentials: true }),
      ]);
      setMatches(m.data.matches || []);
      setTeams(t.data.teams || []);
    } catch (e) {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (m) => {
    setEditing(m.id);
    setForm({
      league: m.league || '',
      season: m.season || '',
      round: m.round || '',
      group_name: m.group_name || '',
      matchday: m.matchday || '',
      home_team_id: m.home_team?.id || '',
      away_team_id: m.away_team?.id || '',
      kickoff: m.kickoff ? m.kickoff.slice(0, 16) : '',
      venue: m.venue || '',
      match_status: m.match_status || 'scheduled',
      prediction_enabled: m.prediction_enabled ?? true,
      home_score: m.home_score ?? '',
      away_score: m.away_score ?? '',
      description: m.description || '',
      visibility: m.visibility || 'published',
      total_prize: m.total_prize || '',
      prize_label: m.prize_label || 'TEBAK SCORE',
      prize_note: m.prize_note || 'HADIAH EVENT TERVERIFIKASI',
      prize_verified: m.prize_verified ?? true,
      prize_breakdown: m.prize_breakdown || [],
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.league || !form.home_team_id || !form.away_team_id || !form.kickoff) {
      toast.error('Isi liga, tim, dan waktu kickoff');
      return;
    }
    if (form.home_team_id === form.away_team_id) {
      toast.error('Tim tuan rumah dan tamu tidak boleh sama');
      return;
    }
    setSaving(true);
    const homeId = parseInt(form.home_team_id);
    const awayId = parseInt(form.away_team_id);
    const countryId = form.country_id === '' || form.country_id == null ? null : parseInt(form.country_id);
    const payload = {
      ...form,
      home_team_id: homeId,
      away_team_id: awayId,
      country_id: countryId,
      home_score: form.home_score === '' || form.home_score == null ? null : parseInt(form.home_score),
      away_score: form.away_score === '' || form.away_score == null ? null : parseInt(form.away_score),
      // Kirim string kickoff apa adanya sebagai WIB naive (admin mengetik jam WIB langsung).
      // Backend akan parse sebagai naive WIB — tidak dikonversi ke UTC.
      kickoff: form.kickoff,
    };
    // Drop empty-string optional fields so backend Pydantic doesn't choke
    Object.keys(payload).forEach((k) => {
      if (payload[k] === '') payload[k] = null;
    });
    try {
      if (editing) {
        await axios.put(`${API_BASE}/admin/matches/${editing}`, payload, { withCredentials: true });
        toast.success('Pertandingan diperbarui');
      } else {
        await axios.post(`${API_BASE}/admin/matches`, payload, { withCredentials: true });
        toast.success('Pertandingan dibuat');
      }
      setShowForm(false);
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus pertandingan ini?')) return;
    try {
      await axios.delete(`${API_BASE}/admin/matches/${id}`, { withCredentials: true });
      toast.success('Pertandingan dihapus');
      fetchAll();
    } catch (e) {
      toast.error('Gagal menghapus');
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1020]" data-testid="admin-matches-page">
      <AdminSidebar active="/admin/matches" />

      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-black text-white mb-2 flex items-center gap-3">
              <Calendar className="text-[#FFD60A]" size={36} /> Jadwal Pertandingan
            </h1>
            <p className="text-gray-400">Kelola semua jadwal pertandingan</p>
          </div>
          <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="add-match-btn">
            <Plus size={18} /> Tambah Pertandingan
          </button>
        </div>

        <div className="glass-card p-6">
          {loading ? (
            <div className="text-gray-400">Loading...</div>
          ) : matches.length === 0 ? (
            <p className="text-gray-400 text-center py-8">Belum ada pertandingan</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-white/10 text-gray-400 text-left">
                  <tr>
                    <th className="py-3 pr-3">Liga</th>
                    <th className="py-3 pr-3">Home vs Away</th>
                    <th className="py-3 pr-3">Kickoff</th>
                    <th className="py-3 pr-3">Status</th>
                    <th className="py-3 pr-3 text-center">Prediksi</th>
                    <th className="py-3 pr-3">Skor</th>
                    <th className="py-3">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-white">
                  {matches.map((m) => (
                    <tr key={m.id} data-testid={`admin-match-row-${m.id}`}>
                      <td className="py-3 pr-3">
                        <div className="font-semibold">{m.league}</div>
                        <div className="text-xs text-gray-500">{m.round || m.group_name || ''}</div>
                      </td>
                      <td className="py-3 pr-3">
                        <div className="text-sm">{m.home_team?.name} <span className="text-gray-500">vs</span> {m.away_team?.name}</div>
                      </td>
                      <td className="py-3 pr-3 text-xs text-gray-300">
                        {new Date(m.kickoff).toLocaleString('id-ID', { day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit', timeZone:'Asia/Jakarta'})} WIB
                      </td>
                      <td className="py-3 pr-3">
                        <span className="text-xs px-2 py-1 rounded bg-white/10">{m.match_status}</span>
                      </td>
                      <td className="py-3 pr-3 text-center">
                        {m.prediction_closed ? (
                          <span className="text-xs text-red-400">Ditutup</span>
                        ) : (
                          <span className="text-xs text-green-400">Buka</span>
                        )}
                      </td>
                      <td className="py-3 pr-3 font-mono">
                        {m.home_score != null && m.away_score != null ? `${m.home_score} - ${m.away_score}` : '-'}
                      </td>
                      <td className="py-3">
                        <div className="flex gap-2">
                          <button onClick={() => openEdit(m)} className="p-2 rounded bg-white/5 hover:bg-white/10 text-blue-400" data-testid={`edit-match-${m.id}`}>
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => handleDelete(m.id)} className="p-2 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400" data-testid={`delete-match-${m.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showForm && (
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="modal-content p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-black text-white">{editing ? 'Edit' : 'Tambah'} Pertandingan</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white"><X size={24} /></button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Liga *</label>
                    <input value={form.league} onChange={(e) => setForm({...form, league: e.target.value})} placeholder="Premier League" data-testid="form-league" />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Season</label>
                    <input value={form.season} onChange={(e) => setForm({...form, season: e.target.value})} placeholder="2025/26" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Round / Matchday</label>
                    <input value={form.round} onChange={(e) => setForm({...form, round: e.target.value})} placeholder="Matchday 15" />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Group</label>
                    <input value={form.group_name} onChange={(e) => setForm({...form, group_name: e.target.value})} placeholder="Group A" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Tim Tuan Rumah *</label>
                    <select value={form.home_team_id} onChange={(e) => setForm({...form, home_team_id: e.target.value})} data-testid="form-home-team">
                      <option value="">Pilih Tim</option>
                      {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Tim Tamu *</label>
                    <select value={form.away_team_id} onChange={(e) => setForm({...form, away_team_id: e.target.value})} data-testid="form-away-team">
                      <option value="">Pilih Tim</option>
                      {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Waktu Kickoff (WIB) *</label>
                  <input type="datetime-local" value={form.kickoff} onChange={(e) => setForm({...form, kickoff: e.target.value})} data-testid="form-kickoff" />
                </div>

                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Venue</label>
                  <input value={form.venue} onChange={(e) => setForm({...form, venue: e.target.value})} placeholder="Old Trafford" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Status Pertandingan</label>
                    <select value={form.match_status} onChange={(e) => setForm({...form, match_status: e.target.value})}>
                      <option value="scheduled">Scheduled</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="live">Live</option>
                      <option value="finished">Finished</option>
                      <option value="postponed">Postponed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Visibility</label>
                    <select value={form.visibility} onChange={(e) => setForm({...form, visibility: e.target.value})}>
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                    </select>
                  </div>
                </div>

                {form.match_status === 'finished' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">Skor Home</label>
                      <input type="number" min="0" value={form.home_score} onChange={(e) => setForm({...form, home_score: e.target.value})} />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">Skor Away</label>
                      <input type="number" min="0" value={form.away_score} onChange={(e) => setForm({...form, away_score: e.target.value})} />
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input type="checkbox" id="pred_enabled" checked={form.prediction_enabled} onChange={(e) => setForm({...form, prediction_enabled: e.target.checked})} />
                  <label htmlFor="pred_enabled" className="text-white text-sm">Prediksi Aktif</label>
                </div>

                <div className="border-t border-white/10 pt-4 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="text-[#FFD60A]" size={18} />
                    <label className="text-white font-bold">Rincian Hadiah (opsional, tampil di halaman Tebak Skor)</label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">Total Hadiah Utama</label>
                      <input value={form.total_prize} onChange={(e) => setForm({...form, total_prize: e.target.value})} placeholder="Rp 2.500.000" data-testid="form-total-prize" />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">Label Hadiah</label>
                      <input value={form.prize_label} onChange={(e) => setForm({...form, prize_label: e.target.value})} placeholder="TEBAK SCORE" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Catatan Hadiah</label>
                    <input value={form.prize_note} onChange={(e) => setForm({...form, prize_note: e.target.value})} placeholder="HADIAH EVENT TERVERIFIKASI" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="prize_verified" checked={form.prize_verified} onChange={(e) => setForm({...form, prize_verified: e.target.checked})} />
                    <label htmlFor="prize_verified" className="text-white text-sm">Tampilkan badge "Terverifikasi & Resmi"</label>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-white text-sm font-semibold">Pembagian Hadiah (Juara 1, 2, 3, dst)</label>
                      <button
                        type="button"
                        onClick={() => setForm({...form, prize_breakdown: [...(form.prize_breakdown || []), {label: '', amount: ''}]})}
                        className="text-xs px-3 py-1 rounded bg-white/5 hover:bg-white/10 text-[#FFD60A] flex items-center gap-1"
                        data-testid="add-prize-row-btn"
                      >
                        <Plus size={12} /> Tambah Baris
                      </button>
                    </div>
                    {(form.prize_breakdown || []).length === 0 && (
                      <p className="text-xs text-gray-500 italic">Belum ada rincian. Klik "Tambah Baris" untuk pembagian juara.</p>
                    )}
                    <div className="space-y-2">
                      {(form.prize_breakdown || []).map((row, idx) => (
                        <div key={idx} className="flex gap-2 items-start" data-testid={`prize-row-${idx}`}>
                          <input
                            value={row.label || ''}
                            onChange={(e) => {
                              const bd = [...form.prize_breakdown];
                              bd[idx] = {...bd[idx], label: e.target.value};
                              setForm({...form, prize_breakdown: bd});
                            }}
                            placeholder="Juara 1 (Skor Tepat)"
                            className="flex-1"
                          />
                          <input
                            value={row.amount || ''}
                            onChange={(e) => {
                              const bd = [...form.prize_breakdown];
                              bd[idx] = {...bd[idx], amount: e.target.value};
                              setForm({...form, prize_breakdown: bd});
                            }}
                            placeholder="Rp 1.500.000"
                            className="flex-1"
                          />
                          <button
                            type="button"
                            onClick={() => setForm({...form, prize_breakdown: form.prize_breakdown.filter((_, i) => i !== idx)})}
                            className="p-2 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-match-btn">
                    <Save size={18} /> {saving ? 'Menyimpan...' : 'Simpan'}
                  </button>
                  <button onClick={() => setShowForm(false)} className="btn-secondary">Batal</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
